import { useEffect, useMemo, useRef, useState } from "react";
import { getScenario, getScenarios } from "../data/scenarios";
import {
    type ApiImportErrorCode,
    type ApiImportStage,
    type ApiImportWorkerMessage,
    DEFAULT_GAME_API_URL,
} from "../domain/api-import";
import type { SavegamePlanResult } from "../domain/savegame-import";
import type { Plan, ScenarioId } from "../domain/types";
import { type LanguageMode, translate, useUiLanguage } from "../i18n";
import { UploadIcon } from "./icons";
import { Modal } from "./Modal";
import { ImportResultPreview } from "./SavegameImportModal";
import { SearchSelect } from "./SearchSelect";

interface ApiImportModalProps {
    readonly initialScenarioId: ScenarioId | undefined;
    readonly replacesCurrentPlan: boolean;
    readonly onClose: () => void;
    readonly onImport: (plan: Plan) => void;
}

interface ApiProcessingState {
    readonly stage: ApiImportStage;
    readonly percent: number;
    readonly running: boolean;
    readonly result: SavegamePlanResult | null;
    readonly error: ApiImportErrorCode | null;
}

const API_URL_STORAGE_KEY = "uptime-datacenter-planner:game-api-url:v1";
const INITIAL_STATE: ApiProcessingState = {
    stage: "connecting",
    percent: 0,
    running: false,
    result: null,
    error: null,
};

function readSavedApiUrl(): string {
    try {
        return window.localStorage.getItem(API_URL_STORAGE_KEY) ?? DEFAULT_GAME_API_URL;
    } catch {
        return DEFAULT_GAME_API_URL;
    }
}

function apiErrorText(code: ApiImportErrorCode, mode: LanguageMode): string {
    if (code === "invalid-url") return translate(mode, "apiErrorInvalidUrl");
    if (code === "unreachable") return translate(mode, "apiErrorUnreachable");
    if (code === "http-error") return translate(mode, "apiErrorHttp");
    if (code === "invalid-response") return translate(mode, "apiErrorInvalidResponse");
    if (code === "too-large") return translate(mode, "apiErrorTooLarge");
    if (code === "unsupported-site") return translate(mode, "savegameErrorUnsupportedSite");
    if (code === "mapping-failed") return translate(mode, "savegameErrorMappingFailed");
    return translate(mode, "savegameWorkerFailed");
}

export function ApiImportModal({
    initialScenarioId,
    replacesCurrentPlan,
    onClose,
    onImport,
}: ApiImportModalProps): React.JSX.Element {
    const { entity, mode, t } = useUiLanguage();
    const [apiUrl, setApiUrl] = useState(readSavedApiUrl);
    const [state, setState] = useState<ApiProcessingState>(INITIAL_STATE);
    const [scenarioId, setScenarioId] = useState<ScenarioId | "">("");
    const workerRef = useRef<Worker | null>(null);

    useEffect(() => () => workerRef.current?.terminate(), []);

    const stageLabel = useMemo(() => {
        if (state.stage === "connecting") return t("apiStageConnecting");
        if (state.stage === "world") return t("apiStageWorld");
        if (state.stage === "racks") return t("apiStageRacks");
        if (state.stage === "equipment") return t("apiStageEquipment");
        if (state.stage === "mapping") return t("apiStageMapping");
        return t("apiStageComplete");
    }, [state.stage, t]);

    function startImport(): void {
        workerRef.current?.terminate();
        const worker = new Worker(new URL("../workers/api-import.worker.ts", import.meta.url), { type: "module" });
        workerRef.current = worker;
        setScenarioId("");
        setState({ ...INITIAL_STATE, running: true, percent: 2 });
        try {
            window.localStorage.setItem(API_URL_STORAGE_KEY, apiUrl.trim());
        } catch {
            // Import still works when browser storage is unavailable.
        }
        worker.onmessage = (event: MessageEvent<ApiImportWorkerMessage>): void => {
            const message = event.data;
            if (message.type === "progress") {
                setState((current) => ({
                    ...current,
                    stage: message.stage,
                    percent: message.percent,
                    running: true,
                    error: null,
                }));
            } else if (message.type === "complete") {
                const supportedScenarios = getScenarios(message.result.plan.project.mapId);
                const reusableScenario = supportedScenarios.find((scenario) => scenario.id === initialScenarioId);
                setScenarioId(reusableScenario?.id ?? "");
                setState({
                    stage: "complete",
                    percent: 100,
                    running: false,
                    result: message.result,
                    error: null,
                });
                worker.terminate();
                workerRef.current = null;
            } else {
                setState((current) => ({ ...current, running: false, error: message.code }));
                worker.terminate();
                workerRef.current = null;
            }
        };
        worker.onerror = (): void => {
            setState((current) => ({ ...current, running: false, error: "worker-failed" }));
            worker.terminate();
            workerRef.current = null;
        };
        worker.postMessage({ apiBaseUrl: apiUrl });
    }

    function useSnapshot(): void {
        if (state.result === null || scenarioId === "") {
            return;
        }
        const scenario = getScenario(scenarioId);
        if (scenario === undefined || !scenario.mapIds.includes(state.result.plan.project.mapId)) {
            return;
        }
        onImport({
            ...state.result.plan,
            project: {
                ...state.result.plan.project,
                scenarioId,
            },
        });
    }

    const footer = (
        <>
            <button className="button" type="button" onClick={onClose}>
                {state.result === null && !state.running ? t("cancel") : t("closeDialog")}
            </button>
            {state.result === null ? (
                <button className="button button-primary" type="button" disabled={state.running} onClick={startImport}>
                    <UploadIcon />
                    {state.error === null ? t("connectToGame") : t("retryApiImport")}
                </button>
            ) : (
                <button
                    className="button button-primary"
                    type="button"
                    disabled={scenarioId === ""}
                    onClick={useSnapshot}
                >
                    <UploadIcon />
                    {t("useApiSnapshot")}
                </button>
            )}
        </>
    );

    return (
        <Modal
            title={t("importFromApi")}
            subtitle={t("importApiSubtitle")}
            className="savegame-import-dialog api-import-dialog"
            onClose={onClose}
            footer={footer}
        >
            <label className="api-import-source">
                <span>{t("apiAddress")}</span>
                <input
                    type="url"
                    value={apiUrl}
                    disabled={state.running || state.result !== null}
                    spellCheck={false}
                    onChange={(event) => setApiUrl(event.target.value)}
                />
                <small>{t("apiAddressHint")}</small>
            </label>

            {!state.running && state.result === null && state.error === null ? null : state.error === null ? (
                <section className="savegame-progress" aria-live="polite">
                    <div className="savegame-progress-heading">
                        <div>
                            <span>{state.result === null ? t("processingApi") : t("savegameReady")}</span>
                            <strong>{stageLabel}</strong>
                        </div>
                        <strong>{state.percent}%</strong>
                    </div>
                    <div
                        className="savegame-progress-track"
                        role="progressbar"
                        aria-label={stageLabel}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={state.percent}
                    >
                        <i style={{ width: `${state.percent}%` }} />
                    </div>
                </section>
            ) : (
                <section className="savegame-error" role="alert">
                    <strong>{t("apiImportFailed")}</strong>
                    <p>{apiErrorText(state.error, mode)}</p>
                </section>
            )}

            {state.result === null ? null : (
                <>
                    <div className="form-field api-import-scenario">
                        <span>{t("scenario")}</span>
                        <SearchSelect
                            ariaLabel={t("scenario")}
                            value={scenarioId}
                            options={getScenarios(state.result.plan.project.mapId).map((scenario) => ({
                                value: scenario.id,
                                label: entity("scenario", scenario.id, scenario.name),
                            }))}
                            onChange={(value) => {
                                const scenario = getScenario(value);
                                if (scenario !== undefined) {
                                    setScenarioId(scenario.id);
                                }
                            }}
                            searchPlaceholder={t("searchScenarios")}
                        />
                        <small>{t("apiScenarioRequired")}</small>
                    </div>
                    <ImportResultPreview result={state.result} replacesCurrentPlan={replacesCurrentPlan} />
                </>
            )}
        </Modal>
    );
}
